import { Box } from '@mantine/core';
import type { TableFilter } from 'types/views';

import type { FilterValues } from '../filters/tableFilterQuery';
import { useFilterOptionsLoader } from '../hooks/useFilterOptionsLoader';
import { useTableFilterState } from '../hooks/useTableFilterState';
import type { FilterResetHandle, FiltersName } from '../hooks/useTableFilters';
import { FilterEditor } from './FilterEditor';
import { FilterList } from './FilterList';

type Props = {
  onSubmit: (filters: FilterValues) => void;
  initialValues: FilterValues;
  filterObject: FilterResetHandle;
  tableName: string;
  filtersAvailable: TableFilter[];
  initialFiltersName: FiltersName;
  setFiltersName: (key: string, values: Record<string, string>) => void;
};

// The view filter form: a searchable list of filters (FilterList) and the
// per-filter editor (FilterEditor). State lives in useTableFilterState; api-backed
// option loading in useFilterOptionsLoader.
export function TableFilters(props: Props) {
  const { listData, loadOptions } = useFilterOptionsLoader();
  const {
    company,
    constants,
    filters,
    filtersSearched,
    currentFilter,
    operator,
    searchText,
    filtersName,
    visibleFilterCategories,
    setFilterValue,
    selectSubmit,
    showFilter,
    setOperator,
    removeCurrentFilter,
    setSearchInput,
  } = useTableFilterState({ ...props, listData });

  // List and editor are mutually exclusive views of the same drawer: selecting a
  // filter replaces the list with its editor, and Back returns to the list. They
  // were previously both mounted with the editor sliding over the list as an
  // absolutely-positioned pane, which read as a second drawer sliding in.
  return (
    <Box>
      {currentFilter ? (
        <FilterEditor
          currentFilter={currentFilter}
          operator={operator}
          setOperator={setOperator}
          onBack={removeCurrentFilter}
          filters={filters}
          setFilterValue={setFilterValue}
          selectSubmit={selectSubmit}
          constants={constants}
          loadOptions={loadOptions}
          listData={listData}
        />
      ) : (
        <FilterList
          searchText={searchText}
          setSearchInput={setSearchInput}
          visibleFilterCategories={visibleFilterCategories}
          filtersSearched={filtersSearched}
          filters={filters}
          filtersName={filtersName}
          constants={constants}
          company={company}
          showFilter={showFilter}
        />
      )}
    </Box>
  );
}
